import React, { useEffect, useRef } from 'react';
import { SmokeBackground } from '../components/ui/spooky-smoke-animation';
import ServicesParallax from '../components/ui/services-parallax';
import TrustedEstablishmentsMarquee from '../components/ui/trusted-establishments-marquee';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import {
  FiArrowRight,
  FiZap,
  FiShield,
  FiTrendingUp,
} from 'react-icons/fi';

import { Link } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';

// ========= EXTRACTED COMPONENTS (Rules of Hooks compliant) =========

const WhyCard = ({ item, index, scrollYProgress }) => {
  const direction = index === 1 ? -1 : 1;
  const cardX = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [direction * 30, 0, direction * -15]
  );
  const smoothCardX = useSpring(cardX, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      style={{ x: smoothCardX }}
      className="text-center p-8 bg-white rounded-2xl shadow-paper border border-gray-100 card-hover"
    >
      <div className="inline-flex p-4 bg-gray-100 rounded-xl text-gray-900 mb-6">
        {item.icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-4 tracking-tight">{item.title}</h3>
      <p className="text-gray-500 leading-relaxed text-sm">{item.description}</p>
    </motion.div>
  );
};

const TestimonialCard = ({ testimonial, index, scrollYProgress }) => {
  const testimonialY = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [30 + index * 10, 0, -15 - index * 5]
  );
  const smoothTestimonialY = useSpring(testimonialY, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      style={{ y: smoothTestimonialY }}
      className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100 card-hover"
    >
      <div className="flex items-center mb-5">
        {[...Array(testimonial.rating)].map((_, i) => (
          <svg key={i} className="w-4 h-4 text-gray-900 fill-current mr-0.5" viewBox="0 0 20 20">
            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
          </svg>
        ))}
      </div>
      <p className="text-gray-500 mb-6 italic leading-relaxed text-sm">"{testimonial.text}"</p>
      <div>
        <div className="font-semibold text-gray-900 text-sm">{testimonial.name}</div>
        <div className="text-xs text-gray-400 uppercase tracking-widest mt-0.5">{testimonial.company}</div>
      </div>
    </motion.div>
  );
};

// ========= MAIN COMPONENT =========

const Home = () => {
  const {
    trackPageView,
    trackNavigation
  } = useAnalytics();

  useEffect(() => {
    trackPageView('/', 'Home - AyTech Soluções Tecnológicas');
  }, [trackPageView]);

  const handleNavigation = (destination) => {
    trackNavigation('home', destination);
  };

  // ========= PARALLAX REFS & SCROLL =========
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const whyRef = useRef(null);
  const testimonialsRef = useRef(null);
  const ctaRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 100]);
  const heroTextOpacity = useTransform(heroProgress, [0, 0.6], [1, 0]);
  const smoothHeroTextY = useSpring(heroTextY, { stiffness: 100, damping: 30 });

  const { scrollYProgress: whyProgress } = useScroll({
    target: whyRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: testimonialsProgress } = useScroll({
    target: testimonialsRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: ctaProgress } = useScroll({
    target: ctaRef,
    offset: ["start end", "end start"],
  });
  const ctaBgY = useTransform(ctaProgress, [0, 1], [30, -30]);
  const smoothCtaBgY = useSpring(ctaBgY, { stiffness: 80, damping: 25 });


  // services data now lives inside ServicesParallax component


  const testimonials = [
    {
      name: "Fernando",
      company: "Tsuki Go",
      text: "O DeliveX ajudou muito a inaugurar nosso delivery. Não ter que pagar taxas de aplicativos nos ajudou a manter o preço justo para nossos clientes e aumentar nossa margem de lucro.",
      rating: 5
    },
    {
      name: "Roberta",
      company: "S.A Casa de Sushi Maringá",
      text: "A IA para WhatsApp é uma mão da roda, responde todos os clientes rapidamente e só preciso intervir quando recebo notificação de que o cliente quer falar com atendente humano.",
      rating: 5
    }
  ];

  const whyItems = [
    {
      icon: <FiZap className="w-7 h-7" />,
      title: "Evolução Constante",
      description: "Sempre evoluindo nossos projetos, desenvolvendo as necessidades dos nossos clientes."
    },
    {
      icon: <FiShield className="w-7 h-7" />,
      title: "Segurança Garantida",
      description: "Implementamos os mais altos padrões de segurança para proteger seus dados e processos."
    },
    {
      icon: <FiTrendingUp className="w-7 h-7" />,
      title: "Empresa em Crescimento",
      description: "Estamos em constante expansão e desenvolvimento, buscando sempre a excelência."
    }
  ];

  return (
    <div className="relative min-h-screen bg-gray-50">

      {/* Shared animated background for every dark section on the landing page */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-[#0a0a0a]">
        <SmokeBackground smokeColor="#2f2f2f" />
      </div>

      {/* ========= HERO SECTION — landingpage style ========= */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Hero Content */}
        <motion.div
          className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          style={{
            y: smoothHeroTextY,
            opacity: heroTextOpacity,
          }}
        >
          <div className="grid min-h-screen grid-cols-1 items-center gap-12 pt-28 pb-20 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)] lg:gap-12 lg:pt-24">
            {/* Left column */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
              className="w-full max-w-xl"
            >

              <h1 className="mb-7 font-display text-5xl font-bold leading-[1.02] tracking-tighter text-white sm:text-6xl xl:text-7xl">
                Tecnologia que coloca seu negócio{' '}
                <span className="relative inline-block whitespace-nowrap">
                  à frente
                  <span className="absolute -bottom-1 left-0 h-px w-full bg-white/60" aria-hidden="true" />
                </span>
              </h1>

              <p className="mb-9 max-w-lg text-base leading-relaxed text-white/80 sm:text-lg">
                Delivery completo, automação de atendimento com Inteligência Artificial, logística e soluções financeiras criadas para simplificar sua operação e acelerar seu negócio.
              </p>

              <Link to="/services" onClick={() => handleNavigation('services')} className="inline-block">
                <motion.span
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-gray-950 transition-colors duration-300 hover:bg-white/90"
                >
                  Conheça nossas soluções
                  <FiArrowRight className="h-4 w-4" />
                </motion.span>
              </Link>

              <div className="mt-12 grid max-w-lg grid-cols-2 border-y border-white/10 sm:mt-14">
                <div className="py-6 pr-5 sm:py-7">
                  <strong className="block text-3xl font-bold tracking-tight text-white sm:text-4xl">99%</strong>
                  <span className="mt-2 block text-xs leading-relaxed text-white/70 sm:text-sm">Satisfação dos clientes</span>
                </div>
                <div className="border-l border-white/10 py-6 pl-5 sm:py-7 sm:pl-7">
                  <strong className="block text-3xl font-bold tracking-tight text-white sm:text-4xl">100%</strong>
                  <span className="mt-2 block text-xs leading-relaxed text-white/70 sm:text-sm">Soluções personalizadas</span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2" aria-label="Avaliação média: 5 de 5 estrelas">
                <div className="flex items-center gap-1" aria-hidden="true">
                  {[...Array(5)].map((_, index) => (
                    <svg key={index} className="h-4 w-4 fill-current text-white" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <strong className="text-sm font-semibold text-white">5,0</strong>
                <span className="text-xs text-white/70 sm:text-sm">Avaliação média dos clientes</span>
              </div>
            </motion.div>

            {/* Right column hero image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
              className="flex min-h-[360px] items-center justify-center sm:min-h-[480px] lg:min-h-[560px]"
            >
              <img
                src="/foto2.png?v=20260925-142649"
                alt="Ecossistema de soluções AyTech com inteligência artificial, gestão de pedidos, rotas e financeiro"
                className="h-auto w-full max-w-full object-contain lg:max-h-[620px]"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        >
          <div className="w-5 h-8 border border-white/30 rounded-full flex justify-center">
            <div className="w-0.5 h-2.5 bg-white/50 rounded-full mt-1.5"></div>
          </div>
        </motion.div>
      </section>

      {/* ========= SERVICES PARALLAX SECTION ========= */}
      <ServicesParallax />

      {/* ========= STATS SECTION ========= */}
      <section ref={statsRef} className="parallax-section pt-28 pb-36 sm:pt-32 sm:pb-40">
        {/* Upside-down wave overlaid on the bottom edge of this section */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 leading-[0]" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            className="block h-16 w-full rotate-180 sm:h-20 lg:h-24"
          >
            <g fill="#fff">
              <path d="M0 1v99c134.3 0 153.7-99 296-99H0Z" opacity=".5" />
              <path d="M1000 4v86C833.3 90 833.3 3.6 666.7 3.6S500 90 333.3 90 166.7 4 0 4h1000Z" opacity=".5" />
              <path d="M617 1v86C372 119 384 1 196 1h421Z" opacity=".5" />
              <path d="M1000 0H0v52C62.5 28 125 4 250 4c250 0 250 96 500 96 125 0 187.5-24 250-48V0Z" />
            </g>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold text-white mt-3 tracking-tight">
              Estabelecimentos que confiam na gente
            </h2>
          </motion.div>

          <TrustedEstablishmentsMarquee />
        </div>
      </section>

      {/* ========= WHY CHOOSE US ========= */}
      <section ref={whyRef} className="parallax-section bg-white pt-10 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-10"
          >
            <span className="text-xs text-gray-400 uppercase tracking-widest font-mono">Diferenciais</span>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-950 mt-3 tracking-tighter" style={{ color: '#0a0a0a' }}>
              Por que escolher a AyTech?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyItems.map((item, index) => (
              <WhyCard
                key={index}
                item={item}
                index={index}
                scrollYProgress={whyProgress}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ========= TESTIMONIALS ========= */}
      <section ref={testimonialsRef} className="pt-16 pb-24 bg-white parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <span className="text-xs text-gray-400 uppercase tracking-widest font-mono">Depoimentos</span>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-950 mt-3 tracking-tighter" style={{ color: '#0a0a0a' }}>
              O que nossos clientes dizem
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                testimonial={testimonial}
                index={index}
                scrollYProgress={testimonialsProgress}
              />
            ))}
          </div>
        </div>

        <div className="glow-line max-w-4xl mx-auto mt-24" />
      </section>

      {/* Organic transition from testimonials into the dark CTA section */}
      <div className="relative z-10 -mb-px leading-[0]" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
          className="block h-16 w-full sm:h-20 lg:h-24"
        >
          <g fill="#f9fafb">
            <path d="M0 1v99c134.3 0 153.7-99 296-99H0Z" opacity=".5" />
            <path d="M1000 4v86C833.3 90 833.3 3.6 666.7 3.6S500 90 333.3 90 166.7 4 0 4h1000Z" opacity=".5" />
            <path d="M617 1v86C372 119 384 1 196 1h421Z" opacity=".5" />
            <path d="M1000 0H0v52C62.5 28 125 4 250 4c250 0 250 96 500 96 125 0 187.5-24 250-48V0Z" />
          </g>
        </svg>
      </div>

      {/* ========= CTA SECTION ========= */}
      <section ref={ctaRef} className="py-24 parallax-section">
        {/* Subtle grid */}
        <motion.div
          className="absolute inset-0 parallax-grid opacity-15"
          style={{ y: smoothCtaBgY }}
        />

        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs text-gray-500 uppercase tracking-widest font-mono">Próximo passo</span>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mt-4 mb-6 tracking-tighter">
              Pronto para transformar seu negócio?
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Entre em contato conosco e descubra como nossas soluções podem revolucionar sua empresa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-950 font-semibold text-sm uppercase tracking-widest rounded-full transition-all duration-300 hover:bg-gray-100 btn-hover"
                  style={{ color: '#0a0a0a' }}
                >
                  Fale com Especialista
                </motion.button>
              </Link>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center px-8 py-4 border border-white/20 text-white font-semibold text-sm uppercase tracking-widest rounded-full transition-all duration-300 hover:border-white/50 hover:bg-white/5"
                >
                  Ver Portfólio
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
